/** Third-party / group shareholder classification (CS Admin). */

export const SHAREHOLDER_FORM_TYPES = {
  GROUP_COMPANY: 'group_company',
  THIRD_PARTY: 'third_party',
  INDIVIDUAL: 'individual',
  EMPLOYEE: 'employee',
};

export const THIRD_PARTY_SUBTYPES = {
  GOVERNMENT: 'government',
  EXTERNAL: 'external',
  PUBLIC: 'public',
};

/** DB / API codes */
export const SHAREHOLDER_API_TYPES = {
  CORPORATE: 'CORPORATE',
  INDIVIDUAL: 'INDIVIDUAL',
  EMPLOYEE: 'EMPLOYEE',
  GOVERNMENT: 'GOVERNMENT',
  EXTERNAL: 'EXTERNAL',
  PUBLIC: 'PUBLIC',
};

const THIRD_PARTY_CODES = new Set([
  SHAREHOLDER_API_TYPES.GOVERNMENT,
  SHAREHOLDER_API_TYPES.EXTERNAL,
  SHAREHOLDER_API_TYPES.PUBLIC,
]);

export function isThirdPartyShareholder(shareholder) {
  const type = String(shareholder?.shareholderType || shareholder?.type || '').toUpperCase();
  return THIRD_PARTY_CODES.has(type);
}

export function isGroupCompanyShareholder(shareholder) {
  const type = String(shareholder?.shareholderType || shareholder?.type || '').toUpperCase();
  return type === SHAREHOLDER_API_TYPES.CORPORATE;
}

/** Shareholders shown as side nodes on ownership diagrams (group + third party). */
export function isSideChartShareholder(shareholder) {
  return isThirdPartyShareholder(shareholder) || isGroupCompanyShareholder(shareholder);
}

/** Visual theme for side-node shareholders on org charts. */
export function getSideShareholderChartTheme(subtypeOrShareholder) {
  const type = typeof subtypeOrShareholder === 'string'
    ? subtypeOrShareholder
    : (subtypeOrShareholder?.shareholderType || subtypeOrShareholder?.type || subtypeOrShareholder?.subtype);
  const isGroup = String(type || '').toUpperCase() === SHAREHOLDER_API_TYPES.CORPORATE;
  if (isGroup) {
    return {
      isGroupCompany: true,
      badge: 'Group company',
      gradient: 'linear-gradient(135deg, #B71C1C 0%, #D32F2F 100%)',
      border: '#7f1d1d',
      selectedBorder: '#FFEBEE',
      handle: '#FFCDD2',
      edgeStroke: '#dc2626',
      edgeMarker: '#dc2626',
      labelBg: '#FFEBEE',
      labelColor: '#B71C1C',
      labelBorder: '#C62828',
      panelBg: '#FFEBEE',
      panelTitle: 'Group company shareholder',
    };
  }
  return {
    isGroupCompany: false,
    badge: 'Third party',
    gradient: 'linear-gradient(135deg, #1565C0 0%, #1976D2 100%)',
    border: '#0D47A1',
    selectedBorder: '#E3F2FD',
    handle: '#BBDEFB',
    edgeStroke: '#1565C0',
    edgeMarker: '#1565C0',
    labelBg: '#E3F2FD',
    labelColor: '#0D47A1',
    labelBorder: '#1565C0',
    panelBg: '#E3F2FD',
    panelTitle: 'Third party shareholder',
  };
}

export function getShareholderTypeLabel(type) {
  const code = String(type || '').toUpperCase();
  const labels = {
    CORPORATE: 'Group company',
    INDIVIDUAL: 'Individual',
    EMPLOYEE: 'Employee / management',
    GOVERNMENT: 'Government',
    EXTERNAL: 'External organisation',
    PUBLIC: 'Public / stock exchange',
  };
  return labels[code] || code || 'Shareholder';
}

export function thirdPartySubtypeToApiType(subtype) {
  switch (subtype) {
    case THIRD_PARTY_SUBTYPES.GOVERNMENT:
      return SHAREHOLDER_API_TYPES.GOVERNMENT;
    case THIRD_PARTY_SUBTYPES.PUBLIC:
      return SHAREHOLDER_API_TYPES.PUBLIC;
    case THIRD_PARTY_SUBTYPES.EXTERNAL:
    default:
      return SHAREHOLDER_API_TYPES.EXTERNAL;
  }
}

export function apiTypeToThirdPartySubtype(apiType) {
  const code = String(apiType || '').toUpperCase();
  if (code === SHAREHOLDER_API_TYPES.GOVERNMENT) return THIRD_PARTY_SUBTYPES.GOVERNMENT;
  if (code === SHAREHOLDER_API_TYPES.PUBLIC) return THIRD_PARTY_SUBTYPES.PUBLIC;
  return THIRD_PARTY_SUBTYPES.EXTERNAL;
}

/** Map add-shareholder form values → API payload type code. */
export function formToApiShareholderType(formData) {
  const { shareholderType, thirdPartySubtype } = formData;
  switch (shareholderType) {
    case SHAREHOLDER_FORM_TYPES.GROUP_COMPANY:
      return SHAREHOLDER_API_TYPES.CORPORATE;
    case SHAREHOLDER_FORM_TYPES.THIRD_PARTY:
      return thirdPartySubtypeToApiType(thirdPartySubtype);
    case SHAREHOLDER_FORM_TYPES.INDIVIDUAL:
      return SHAREHOLDER_API_TYPES.INDIVIDUAL;
    case SHAREHOLDER_FORM_TYPES.EMPLOYEE:
      return SHAREHOLDER_API_TYPES.EMPLOYEE;
    default:
      return SHAREHOLDER_API_TYPES.CORPORATE;
  }
}

/** Map stored API type → form shareholderType + optional thirdPartySubtype. */
export function apiToFormShareholderFields(shareholder) {
  const apiType = String(shareholder?.shareholderType || shareholder?.type || '').toUpperCase();

  if (THIRD_PARTY_CODES.has(apiType)) {
    return {
      shareholderType: SHAREHOLDER_FORM_TYPES.THIRD_PARTY,
      thirdPartySubtype: apiTypeToThirdPartySubtype(apiType),
    };
  }
  if (apiType === SHAREHOLDER_API_TYPES.INDIVIDUAL) {
    return { shareholderType: SHAREHOLDER_FORM_TYPES.INDIVIDUAL, thirdPartySubtype: '' };
  }
  if (apiType === SHAREHOLDER_API_TYPES.EMPLOYEE) {
    return { shareholderType: SHAREHOLDER_FORM_TYPES.EMPLOYEE, thirdPartySubtype: '' };
  }
  return { shareholderType: SHAREHOLDER_FORM_TYPES.GROUP_COMPANY, thirdPartySubtype: '' };
}

export function getShareholderDisplayColors(type) {
  if (isThirdPartyShareholder({ shareholderType: type })) {
    return { bg: '#E3F2FD', color: '#1565C0', border: '#1976D2' };
  }
  const code = String(type || '').toUpperCase();
  if (code === SHAREHOLDER_API_TYPES.CORPORATE) {
    return { bg: '#FFEBEE', color: '#B71C1C', border: '#C62828' };
  }
  if (code === SHAREHOLDER_API_TYPES.EMPLOYEE) {
    return { bg: '#FFF3E0', color: '#E65100', border: '#F57C00' };
  }
  return { bg: '#F3E5F5', color: '#6A1B9A', border: '#8E24AA' };
}
